import {
  faAlignCenter,
  faAlignJustify,
  faAlignLeft,
  faAlignRight,
  faCode,
  faH,
  faH1,
  faH2,
  faH3,
  faList,
  faListCheck,
  faListOl,
  faListUl,
  faQuoteLeft,
  faText,
} from '@fortawesome/pro-regular-svg-icons';
import { Editor } from '@tiptap/core';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { snakeCase } from 'lodash';
import { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { ToolbarButtonIcon, ToolbarDropdownTriggerButton } from './components';
import { ToolBarItemProps } from './types';
import { Dropdown } from '../../dropdown/dropdown';

enum BasicFormattingCommand {
  Text = 'text',
  HeadingLevelOne = 'heading-level-one',
  HeadingLevelTwo = 'heading-level-two',
  HeadingLevelThree = 'heading-level-three',
  BulletList = 'bullet-list',
  OrderedList = 'ordered-list',
  TaskList = 'task-list',
  Blockquote = 'blockquote',
  Code = 'code',
  TextAlignLeft = 'text-align-left',
  TextAlignCenter = 'text-align-center',
  TextAlignRight = 'text-align-right',
  TextAlignJustify = 'text-align-justify',
}
const BlockLevelCommand = [
  BasicFormattingCommand.HeadingLevelOne,
  BasicFormattingCommand.HeadingLevelTwo,
  BasicFormattingCommand.HeadingLevelThree,
  BasicFormattingCommand.BulletList,
  BasicFormattingCommand.OrderedList,
  BasicFormattingCommand.TaskList,
  BasicFormattingCommand.Blockquote,
];
const getActiveCommand = (editor: Editor): BasicFormattingCommand =>
  (Object.entries(commandMap).find(([_, command]) =>
    command.isActive(editor),
  )?.[0] ?? BasicFormattingCommand.Text) as BasicFormattingCommand;
const commandMap = {
  [BasicFormattingCommand.Text]: {
    icon: <ToolbarButtonIcon icon={faText} />,
    isActive: () => false,
    execute: (editor: Editor) => {
      const activeCommand = getActiveCommand(editor);

      if (BlockLevelCommand.includes(activeCommand)) {
        commandMap[activeCommand].execute(editor);
      }
    },
  },
  [BasicFormattingCommand.Blockquote]: {
    icon: <ToolbarButtonIcon icon={faQuoteLeft} />,
    isActive: (editor: Editor) => editor.isActive('blockquote'),
    execute: (editor: Editor) =>
      editor.chain().focus().toggleBlockquote().run(),
  },
  [BasicFormattingCommand.Code]: {
    icon: <ToolbarButtonIcon icon={faCode} />,
    isActive: (editor: Editor) => editor.isActive('code'),
    execute: (editor: Editor) => editor.chain().focus().toggleCode().run(),
  },
  [BasicFormattingCommand.HeadingLevelOne]: {
    icon: <ToolbarButtonIcon icon={faH1} />,
    isActive: (editor: Editor) => editor.isActive('heading', { level: 1 }),
    execute: (editor: Editor) =>
      editor.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  [BasicFormattingCommand.HeadingLevelTwo]: {
    icon: <ToolbarButtonIcon icon={faH2} />,
    isActive: (editor: Editor) => editor.isActive('heading', { level: 2 }),
    execute: (editor: Editor) =>
      editor.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  [BasicFormattingCommand.HeadingLevelThree]: {
    icon: <ToolbarButtonIcon icon={faH3} />,
    isActive: (editor: Editor) => editor.isActive('heading', { level: 3 }),
    execute: (editor: Editor) =>
      editor.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  [BasicFormattingCommand.BulletList]: {
    icon: <ToolbarButtonIcon icon={faListUl} />,
    isActive: (editor: Editor) => editor.isActive('bulletList'),
    execute: (editor: Editor) =>
      editor.chain().focus().toggleBulletList().run(),
  },
  [BasicFormattingCommand.OrderedList]: {
    icon: <ToolbarButtonIcon icon={faListOl} />,
    isActive: (editor: Editor) => editor.isActive('orderedList'),
    execute: (editor: Editor) =>
      editor.chain().focus().toggleOrderedList().run(),
  },
  [BasicFormattingCommand.TaskList]: {
    icon: <ToolbarButtonIcon icon={faListCheck} />,
    isActive: (editor: Editor) => editor.isActive('taskList'),
    execute: (editor: Editor) => editor.chain().focus().toggleTaskList().run(),
  },
  [BasicFormattingCommand.TextAlignLeft]: {
    icon: <ToolbarButtonIcon icon={faAlignLeft} />,
    isActive: (editor: Editor) => editor.isActive({ textAlign: 'left' }),
    execute: (editor: Editor) =>
      editor.chain().focus().setTextAlign('left').run(),
  },
  [BasicFormattingCommand.TextAlignCenter]: {
    icon: <ToolbarButtonIcon icon={faAlignCenter} />,
    isActive: (editor: Editor) => editor.isActive({ textAlign: 'center' }),
    execute: (editor: Editor) =>
      editor.chain().focus().setTextAlign('center').run(),
  },
  [BasicFormattingCommand.TextAlignRight]: {
    icon: <ToolbarButtonIcon icon={faAlignRight} />,
    isActive: (editor: Editor) => editor.isActive({ textAlign: 'right' }),
    execute: (editor: Editor) =>
      editor.chain().focus().setTextAlign('right').run(),
  },
  [BasicFormattingCommand.TextAlignJustify]: {
    icon: <ToolbarButtonIcon icon={faAlignJustify} />,
    isActive: (editor: Editor) => editor.isActive({ textAlign: 'justify' }),
    execute: (editor: Editor) =>
      editor.chain().focus().setTextAlign('justify').run(),
  },
};

const commandAsMenuItem = (command: BasicFormattingCommand) => {
  return {
    key: command,
    label: (
      <FormattedMessage
        id={`editor.toolbar.basic_formatting.${snakeCase(command)}`}
      />
    ),
    icon: commandMap[command].icon,
  };
};
const commandMenuItems = [
  commandAsMenuItem(BasicFormattingCommand.Text),
  commandAsMenuItem(BasicFormattingCommand.Blockquote),
  commandAsMenuItem(BasicFormattingCommand.Code),
  {
    label: <FormattedMessage id="editor.toolbar.basic_formatting.heading" />,
    icon: <ToolbarButtonIcon icon={faH} />,
    children: [
      commandAsMenuItem(BasicFormattingCommand.HeadingLevelOne),
      commandAsMenuItem(BasicFormattingCommand.HeadingLevelTwo),
      commandAsMenuItem(BasicFormattingCommand.HeadingLevelThree),
    ],
  },
  {
    label: <FormattedMessage id="editor.toolbar.basic_formatting.list" />,
    icon: <ToolbarButtonIcon icon={faList} />,
    children: [
      commandAsMenuItem(BasicFormattingCommand.BulletList),
      commandAsMenuItem(BasicFormattingCommand.OrderedList),
      commandAsMenuItem(BasicFormattingCommand.TaskList),
    ],
  },
  {
    label: <FormattedMessage id="editor.toolbar.basic_formatting.text_align" />,
    icon: <ToolbarButtonIcon icon={faAlignLeft} />,
    children: [
      commandAsMenuItem(BasicFormattingCommand.TextAlignLeft),
      commandAsMenuItem(BasicFormattingCommand.TextAlignCenter),
      commandAsMenuItem(BasicFormattingCommand.TextAlignRight),
      commandAsMenuItem(BasicFormattingCommand.TextAlignJustify),
    ],
  },
];

export const BasicFormattingCommands: FC<ToolBarItemProps> = ({
  editor,
  afterCommandExecuted,
}) => {
  const activeCommand = useMemo(() => {
    return getActiveCommand(editor);
  }, [editor]);
  const activeCommandLabel = useMemo(() => {
    return commandAsMenuItem(activeCommand).label;
  }, [activeCommand]);

  const dropdownMenuProps = useMemo(() => {
    return {
      selectedKeys: [activeCommand],
      onMouseDown: (e) => {
        e.preventDefault();
        e.stopPropagation();
      },
      onClick: ({ key, domEvent }) => {
        domEvent.preventDefault();
        domEvent.stopPropagation();
        commandMap[key].execute(editor);
        afterCommandExecuted?.();
      },
      items: commandMenuItems as ItemType[],
    };
  }, [activeCommand, afterCommandExecuted, editor]);

  return (
    <Dropdown menu={dropdownMenuProps}>
      <ToolbarDropdownTriggerButton icon={commandMap[activeCommand].icon}>
        {activeCommandLabel}
      </ToolbarDropdownTriggerButton>
    </Dropdown>
  );
};
